import { QuestionCircleOutlined } from '@ant-design/icons';
import AppCard from '@nxsys/components/AppCard';
import { AppConfirmation } from '@nxsys/components/AppConfirmationModal';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppPayFrequencySelect from '@nxsys/components/AppPayFrequencySelect';
import { PayFrequencyValue } from '@nxsys/components/AppPayFrequencySelect/type';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxWeekMonthSelect from '@nxsys/components/AppTaxWeekMonthSelect';
import {
  useApprovePayment,
  useGetScheduleForApprovePayment,
  useGetSubmissionsForApprovePayment,
} from '@nxsys/hooks/api/report';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { PaymentFrequencyType } from '@nxsys/types/models/Common';
import { showCurrencyByCountry } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Button, Col, Row, Spin, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

type ApprovePaymentModalProps = {
  isOpen: boolean;
  toggle: () => void;
};

const ApprovePaymentModal: React.FC<ApprovePaymentModalProps> = ({
  isOpen,
  toggle,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);

  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [approving, setApproving] = useState(false);

  // Filter states
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [selectedPayFrequency, setSelectedPayFrequency] =
    useState<PayFrequencyValue | null>(null);
  const [selectedTaxPeriod, setSelectedTaxPeriod] = useState<number | null>(
    null,
  );

  // Data states
  const [scheduleData, setScheduleData] = useState<any>(null);
  const [submissionsData, setSubmissionsData] = useState([]);
  const [totalContributedPension, setTotalContributedPension] = useState(0);

  // Load schedule data
  const { run: getScheduleForApprovePayment } =
    useGetScheduleForApprovePayment();
  const loadScheduleData = async () => {
    if (!selectedCompany || !selectedPayFrequency || !selectedTaxPeriod) {
      return;
    }

    setScheduleLoading(true);
    try {
      const response = await getScheduleForApprovePayment({
        companyId: selectedCompany.id,
        payFrequency: selectedPayFrequency,
        taxPeriod: selectedTaxPeriod,
      });
      setScheduleData(response.data?.schedule || null);
    } catch (error) {
      console.error(error);
      console.error('Schedule API error:', error);
    } finally {
      setScheduleLoading(false);
    }
  };

  // Load submissions data
  const { run: getSubmissionsForApprovePayment } =
    useGetSubmissionsForApprovePayment();
  const loadSubmissionsData = async () => {
    if (!selectedCompany || !selectedPayFrequency || !selectedTaxPeriod) {
      return;
    }

    setSubmissionsLoading(true);
    try {
      const response = await getSubmissionsForApprovePayment({
        companyId: selectedCompany.id,
        payFrequency: selectedPayFrequency,
        taxPeriod: selectedTaxPeriod,
      });
      const submissions = response.data?.submissions || [];
      setSubmissionsData(submissions);
      setTotalContributedPension(response.data?.totalContributedPension || 0);
    } catch (error) {
      console.error(error);
    } finally {
      setSubmissionsLoading(false);
    }
  };

  // Approve payment function
  const { run: approvePayment } = useApprovePayment();
  const handleApprovePayment = async () => {
    if (!scheduleData?.id) {
      showMessage({
        type: 'warning',
        content: 'No schedule data available for approval',
      });
      return;
    }

    const approveNow = async () => {
      setApproving(true);
      try {
        await approvePayment(scheduleData.id);
        showMessage({
          type: 'success',
          content: 'Payment approved successfully!',
        });
        toggle();
      } catch (error: any) {
        console.error(error);
      } finally {
        setApproving(false);
      }
    };

    const nestTotal = scheduleData.totalAmount || 0;
    const payrollTotal = totalContributedPension;
    const difference = Math.abs(payrollTotal - nestTotal);
    const hasDifference = difference >= 0.01;

    if (hasDifference) {
      AppConfirmation.open({
        title: 'Payment amounts do not match',
        paragraph: (
          <div>
            <p>
              The total contribution calculated by Payroll does not match the
              amount NEST is expecting.
            </p>
            <div
              style={{
                background: '#fafafa',
                border: '1px solid #eee',
                borderRadius: 6,
                padding: 12,
                marginBottom: 12,
              }}
            >
              <div>
                - Payroll total: <strong>{formatMoney(payrollTotal, showCurrencyByCountry(payrollCountry))}</strong>
              </div>
              <div>
                - NEST expected total: <strong>{formatMoney(nestTotal, showCurrencyByCountry(payrollCountry))}</strong>
              </div>
              <div>
                - Difference: <strong>{formatMoney(difference, showCurrencyByCountry(payrollCountry))}</strong>
              </div>
            </div>
            <p style={{ margin: '12px 0 6px' }}>
              <strong>POSSIBLE CAUSES:</strong>
            </p>
            <ul style={{ paddingLeft: 18 }}>
              <li>
                Someone may have updated NEST online (e.g. member leaver, amount
                change) not reflected in payroll.
              </li>
              <li>
                A member's earnings or contribution rate in payroll differs from
                what NEST has.
              </li>
              <li>
                A member has been marked as leaver or opt-out on NEST but
                payroll has not recorded it yet.
              </li>
            </ul>
            <p style={{ margin: '12px 0 6px' }}>
              <strong>NEXT ACTIONS:</strong>
            </p>
            <ol style={{ paddingLeft: 18 }}>
              <li>
                Identify employees with discrepancies and correct the data in
                the payroll software.
              </li>
              <li>
                Re-run the "Submit contributions to NEST" process for this
                schedule.
              </li>
              <li>
                Only when NEST confirms the totals match should you proceed to
                "Approve Payment".
              </li>
            </ol>
          </div>
        ),
        type: 'warning',
        icon: 'IconWarning',
        okText: `Approve ${formatMoney(nestTotal, showCurrencyByCountry(payrollCountry)).replace(' ', '')} anyway`,
        cancelText: 'Cancel',
        onConfirm: async () => {
          await approveNow();
        },
      });
      return;
    }

    await approveNow();
  };

  // Auto load data when all filters are selected
  const autoLoadData = React.useCallback(() => {
    if (selectedCompany && selectedPayFrequency && selectedTaxPeriod) {
      Promise.all([loadScheduleData(), loadSubmissionsData()]);
    }
  }, [selectedCompany, selectedPayFrequency, selectedTaxPeriod]);

  // Handle close
  const handleClose = () => {
    setSelectedCompany(null);
    setSelectedPayFrequency(null);
    setSelectedTaxPeriod(null);
    setScheduleData(null);
    setSubmissionsData([]);
    setTotalContributedPension(0);
    toggle();
  };

  useEffect(() => {
    if (!isOpen) {
      handleClose();
    }
  }, [isOpen]);

  // Reset tax period when pay frequency changes
  useEffect(() => {
    setSelectedTaxPeriod(null);
    setScheduleData(null);
    setSubmissionsData([]);
  }, [selectedPayFrequency]);

  // Reset data when company changes
  useEffect(() => {
    setScheduleData(null);
    setSubmissionsData([]);
    setTotalContributedPension(0);
  }, [selectedCompany]);

  // Auto load data when all filters are selected
  useEffect(() => {
    autoLoadData();
  }, [autoLoadData]);

  // Submissions table columns
  const submissionsColumns = [
    {
      title: 'Payroll ID',
      dataIndex: 'payrollId',
      key: 'payrollId',
      width: 120,
    },
    {
      title: 'Employee ID',
      dataIndex: 'subcontractorId',
      key: 'employeeId',
      width: 120,
    },
    {
      title: messages['common.employeeAndSubcontractor'],
      dataIndex: 'subcontractor',
      key: 'subcontractor',
      width: 200,
      render: (subcontractor: any) => subcontractor?.fullName || 'N/A',
    },
    {
      title: 'Gross for Pension',
      dataIndex: 'grossForPension',
      key: 'grossForPension',
      width: 150,
      render: (value: number) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      title: 'Contributed Pension',
      dataIndex: 'contributedPension',
      key: 'contributedPension',
      width: 150,
      render: (value: number) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      title: 'EE Pension',
      dataIndex: 'eePension',
      key: 'eePension',
      width: 120,
      render: (value: number) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      title: 'ER Pension',
      dataIndex: 'erPension',
      key: 'erPension',
      width: 120,
      render: (value: number) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
  ];

  return (
    <AppModalContainer
      title="Approve Payment"
      isOpen={isOpen}
      toggle={toggle}
      width={1200}
    >
      {/* Note Section */}
      <div
        style={{
          background: '#f6f8ff',
          border: '1px solid #d6e4ff',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          fontSize: '14px',
          lineHeight: '1.5',
        }}
      >
        <div style={{ marginBottom: '12px', color: '#333', fontWeight: '500' }}>
          <strong>Important Payment Approval Information</strong>
        </div>
        <div style={{ color: '#666', marginBottom: '8px' }}>
          You must review and approve the payment for each pension contribution
          schedule before processing. Ensure all contributions have been
          successfully submitted and verified before proceeding with payment
          approval.
        </div>
        <div style={{ color: '#666', fontSize: '13px' }}>
          The schedule information below is populated based on your selected
          filters. Verify the total amount matches the sum of all contributed
          pensions before approval.
        </div>
      </div>

      {/* Filter Section */}
      <AppCard title="Filters" size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} className="p-[20px]">
          <Col sm={24} md={8} className="w-full">
            <AppSelectSync
              type="company"
              apiType="searchCompany"
              placeholder={messages['common.pleaseSelectCompany']}
              onChangeValue={(option) => {
                setSelectedCompany((option as any)?.fullData);
              }}
              className="w-full"
            />
          </Col>
          <Col sm={24} md={8} className="w-full">
            <AppPayFrequencySelect
              placeholder={messages['common.pleaseSelectPayFrequency']}
              onChange={(value) => setSelectedPayFrequency(value)}
              value={selectedPayFrequency}
            />
          </Col>
          <Col sm={24} md={8} className="w-full">
            <TaxWeekMonthSelect
              placeholder={messages['common.pleaseSelectTaxPeriod']}
              onFilter={(value) =>
                setSelectedTaxPeriod(value.taxPeriod as number)
              }
              value={selectedTaxPeriod}
              paymentFrequency={selectedPayFrequency as PaymentFrequencyType}
              disabled={!selectedPayFrequency}
            />
          </Col>
        </Row>
      </AppCard>

      {/* Schedule Information Section */}
      <AppCard
        title="Schedule Information"
        size="small"
        style={{ marginBottom: 16 }}
        bodyStyle={{ padding: '20px' }}
      >
        <Spin spinning={scheduleLoading}>
          {scheduleData ? (
            <div style={{ display: 'flex', gap: '32px' }}>
              {/* Left Column */}
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                <div
                  style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                    padding: '10px 18px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    height: '100%',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 0',
                      borderBottom: '1px solid #dee2e6',
                    }}
                  >
                    <span style={{ fontWeight: '600', color: '#495057' }}>
                      Schedule reference:
                    </span>
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        color: '#212529',
                      }}
                    >
                      {scheduleData.scheduleId || 'N/A'}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 0',
                      borderBottom: '1px solid #dee2e6',
                    }}
                  >
                    <span style={{ fontWeight: '600', color: '#495057' }}>
                      Payment Source:
                    </span>
                    <span style={{ color: '#212529' }}>
                      {scheduleData.paymentSourceName || 'N/A'}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 0',
                      borderBottom: '1px solid #dee2e6',
                    }}
                  >
                    <span style={{ fontWeight: '600', color: '#495057' }}>
                      Earnings period:
                    </span>
                    <span style={{ color: '#212529' }}>
                      {scheduleData.startDate && scheduleData.endDate
                        ? `${new Date(
                            scheduleData.startDate,
                          ).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                          })} - ${new Date(
                            scheduleData.endDate,
                          ).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}`
                        : 'N/A'}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 0',
                      borderBottom: '1px solid #dee2e6',
                    }}
                  >
                    <span style={{ fontWeight: '600', color: '#495057' }}>
                      Due Date:
                    </span>
                    <span style={{ color: '#212529' }}>
                      {scheduleData.dueDate
                        ? new Date(scheduleData.dueDate).toLocaleDateString(
                            'en-GB',
                            {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            },
                          )
                        : 'N/A'}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 0',
                    }}
                  >
                    <span style={{ fontWeight: '600', color: '#495057' }}>
                      Schedule status:
                    </span>
                    <span style={{ color: '#212529' }}>
                      {scheduleData.scheduleStatus === 'D' ? 'Due' : 'Overdue'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    background: '#e3f2fd',
                    borderRadius: '6px',
                    border: '1px solid #bbdefb',
                  }}
                >
                  <span style={{ fontWeight: '600', color: '#1565c0' }}>
                    Total Amount From Nest:
                  </span>
                  <span
                    style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#1565c0',
                    }}
                  >
                    {formatMoney(
                      scheduleData.totalAmount || 0,
                      showCurrencyByCountry(payrollCountry),
                    )}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    background: '#e8f5e8',
                    borderRadius: '6px',
                    border: '1px solid #c3e6c3',
                  }}
                >
                  <span style={{ fontWeight: '600', color: '#2d5a2d' }}>
                    Total Contributed Pension:
                  </span>
                  <span
                    style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#2d5a2d',
                    }}
                  >
                    {formatMoney(
                      totalContributedPension,
                      showCurrencyByCountry(payrollCountry),
                    )}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    background:
                      Math.abs(
                        (scheduleData.totalAmount || 0) -
                          totalContributedPension,
                      ) < 0.01
                        ? '#e8f5e8'
                        : '#ffeaea',
                    borderRadius: '6px',
                    border:
                      Math.abs(
                        (scheduleData.totalAmount || 0) -
                          totalContributedPension,
                      ) < 0.01
                        ? '1px solid #c3e6c3'
                        : '1px solid #ffcccc',
                  }}
                >
                  <span
                    style={{
                      fontWeight: '600',
                      color:
                        Math.abs(
                          (scheduleData.totalAmount || 0) -
                            totalContributedPension,
                        ) < 0.01
                          ? '#2d5a2d'
                          : '#d32f2f',
                    }}
                  >
                    Difference:
                    {Math.abs(
                      (scheduleData.totalAmount || 0) - totalContributedPension,
                    ) >= 0.01 && (
                      <span style={{ marginLeft: 6 }}>
                        <Tooltip
                          placement="top"
                          title="The difference between Nxsys and NEST may occur if you manually submitted on the NEST website or from other systems. Nxsys cannot detect those submissions, so the amounts may differ."
                        >
                          <QuestionCircleOutlined
                            style={{ color: '#8c8c8c', cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </span>
                    )}
                  </span>
                  <span
                    style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color:
                        Math.abs(
                          (scheduleData.totalAmount || 0) -
                            totalContributedPension,
                        ) < 0.01
                          ? '#2d5a2d'
                          : '#d32f2f',
                    }}
                  >
                    {formatMoney(
                      (scheduleData.totalAmount || 0) - totalContributedPension,
                      showCurrencyByCountry(payrollCountry),
                    )}
                  </span>
                </div>

                {/* Approve Payment Button */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: '8px',
                    paddingTop: '16px',
                    borderTop: '1px solid #e9ecef',
                  }}
                >
                  <Button
                    type="primary"
                    loading={approving}
                    disabled={!scheduleData?.id}
                    onClick={handleApprovePayment}
                    size="large"
                    style={{
                      minWidth: '140px',
                      height: '40px',
                      fontSize: '14px',
                      fontWeight: '600',
                    }}
                  >
                    Approve Payment
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div
              style={{
                textAlign: 'center',
                color: '#999',
                padding: '40px 20px',
                background: '#f8f9fa',
                borderRadius: '6px',
                border: '1px dashed #dee2e6',
              }}
            >
              <div style={{ fontSize: '16px', marginBottom: '8px' }}>📋</div>
              <div>Please select all filters to load schedule information</div>
            </div>
          )}
        </Spin>
      </AppCard>

      {/* Submissions List Section */}
      <AppCard title="Submissions List" size="small">
        <Spin spinning={submissionsLoading}>
          <AppTableContainer
            rowKey="id"
            data={submissionsData ?? []}
            loading={submissionsLoading}
            page={0}
            total={submissionsData.length}
            pageSize={submissionsData.length}
            scroll={{ x: 800 }}
            columns={submissionsColumns}
          />
        </Spin>
      </AppCard>
    </AppModalContainer>
  );
};

export default ApprovePaymentModal;
